'use client';

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { managerNavigation, navigationLinkActive } from './navigation';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/design-system/utils/cn';
import { Button } from '@/design-system';
import { usePlatformBranding } from '@/core/platform-branding';
import { useAuth } from '@/core/auth';
import { useVersion } from '@/core/hooks/useVersion';
import { useAffiliateProgramQuery } from '@/features/settings';


interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  enableTransition: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

export function Sidebar({
  isOpen,
  isCollapsed,
  enableTransition,
  onClose,
  onToggleCollapse,
}: SidebarProps) {
  const { branding } = usePlatformBranding();
  const { user } = useAuth();
  const affiliateProgram = useAffiliateProgramQuery(!!user && user.role !== 'SUPPORT');
  const navItems = managerNavigation(user?.role, affiliateProgram.data?.enabled ?? false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { managerDisplay, backendDisplay } = useVersion();
  const location = useLocation();
  const pathname = location.pathname;

  const navLinkActiveClass = 'bg-white/20 text-white font-medium shadow-sm';
  const logoSrc = branding.logoUrlDark || branding.logoUrl;

  const logoMark = logoSrc ? (
    <img src={logoSrc} alt="" className="h-6 w-6 object-contain" />
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" className="fill-primary" />
    </svg>
  );

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      <aside
        className={cn(
          'fixed left-0 top-0 bottom-0 bg-primary text-white flex flex-col z-50 ease-in-out border-r border-white/10',
          enableTransition ? 'transition-all duration-300' : 'transition-none',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          isCollapsed ? 'w-20' : 'w-64'
        )}
      >
        {/* Header */}
        <div className="p-4 h-16 flex items-center justify-between border-b border-white/10 flex-shrink-0">
          <div className={cn('flex items-center gap-3 transition-all duration-300', isCollapsed ? 'opacity-0 scale-50' : 'opacity-100 scale-100')}>
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden p-0.5">
              {logoMark}
            </div>
            {!isCollapsed && (
              <h1 className="font-bold text-lg tracking-tight text-white truncate max-w-[9rem]">
                {branding.platformName}
              </h1>
            )}
          </div>

          {isCollapsed && (
            <div className="absolute left-1/2 -translate-x-1/2 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-lg overflow-hidden p-0.5">
              {logoSrc ? (
                <img src={logoSrc} alt="" className="h-5 w-5 object-contain" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" className="fill-primary" />
                </svg>
              )}
            </div>
          )}

          <Button variant="ghost" size="sm" onClick={onClose} className="lg:hidden p-1.5 text-white" aria-label="Fechar menu">
            <X size={20} />
          </Button>

          <Button variant="ghost" size="sm"
            onClick={onToggleCollapse}
            className="hidden lg:flex absolute -right-3 top-8 h-6 w-6 rounded-full border border-gray-200 bg-white p-0 text-primary shadow-md hover:scale-110 transition-all z-[60]"
            aria-label="Recolher ou expandir menu"
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </Button>
        </div>

        {/* Navigation */}
        <nav aria-label="Menu principal" className="flex-1 space-y-1.5 overflow-y-auto px-3 py-6">
          {navItems.map((item) => {
            const Icon = item.icon;
            if ('children' in item) {
              const active = item.children.some((child) => navigationLinkActive(pathname, child.href));
              const expanded = !isCollapsed && (expandedGroups[item.name] ?? active);
              const groupId = 'navigation-' + item.name.toLowerCase().replace(/ /g, '-');
              return <div key={item.name}>
                <Button
                  type="button" variant="ghost" aria-label={item.name} aria-expanded={expanded} aria-controls={groupId}
                  title={isCollapsed ? item.name : undefined}
                  onClick={() => {
                    setExpandedGroups((groups) => ({ ...groups, [item.name]: isCollapsed || !expanded }));
                    if (isCollapsed) onToggleCollapse();
                  }}
                  className={cn('h-auto w-full rounded-lg px-3 py-2.5 text-white/80 hover:bg-white/10 hover:text-white', active && navLinkActiveClass, isCollapsed ? 'justify-center' : 'justify-start gap-3')}
                >
                  <Icon size={20} className="shrink-0" aria-hidden />
                  {!isCollapsed && <><span className="flex-1 text-left text-sm">{item.name}</span><ChevronDown size={16} aria-hidden className={cn('transition-transform', expanded && 'rotate-180')} /></>}
                </Button>
                <div id={groupId} hidden={!expanded} className="ml-5 mt-1 space-y-1 border-l border-white/15 pl-3">
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const childActive = navigationLinkActive(pathname, child.href);
                    return <Link key={child.href} to={child.href} onClick={onClose} aria-current={childActive ? 'page' : undefined}
                      className={cn('flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors', childActive ? navLinkActiveClass : 'text-white/75 hover:bg-white/10 hover:text-white')}>
                      <ChildIcon size={16} className="shrink-0" aria-hidden />{child.name}
                    </Link>;
                  })}
                </div>
              </div>;
            }
            const active = navigationLinkActive(pathname, item.href);
            return <Link key={item.href} to={item.href} onClick={onClose} aria-label={item.name}
              title={isCollapsed ? item.name : undefined} aria-current={active ? 'page' : undefined}
              className={cn('flex w-full items-center rounded-lg px-3 py-2.5 transition-colors', active ? navLinkActiveClass : 'text-white/80 hover:bg-white/10 hover:text-white', isCollapsed ? 'justify-center' : 'gap-3')}>
              <Icon size={20} className="shrink-0" aria-hidden />
              {!isCollapsed && <span className="whitespace-nowrap text-sm">{item.name}</span>}
            </Link>;
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 py-6 border-t border-white/10 space-y-1.5 flex-shrink-0">
          <div
            className={cn(
              'px-3 py-2.5 rounded-lg text-white/70',
              isCollapsed ? 'flex justify-center items-center' : 'space-y-0.5'
            )}
          >
            {isCollapsed ? (
              <span className="text-xs opacity-70 truncate">
                {managerDisplay ?? backendDisplay ?? '—'}
              </span>
            ) : (
              <>
                {managerDisplay && (
                  <p className="text-xs opacity-70 truncate">{managerDisplay}</p>
                )}
                {backendDisplay && (
                  <p className="text-xs opacity-70 truncate">{backendDisplay}</p>
                )}
                {!managerDisplay && !backendDisplay && (
                  <p className="text-xs opacity-70">—</p>
                )}
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
